import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { CalendarDaysIcon, ClockIcon } from "@heroicons/react/24/outline";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { Alert } from "@/components/ui/Alert";
import {
  usePersonalNavigation,
  resolveVisiblePersonalHubCards,
  type PersonalHubCard,
} from "@/lib/personalNav";
import { getUserLocalLogin, useCurrentUser } from "@/lib/auth";
import { normGroups } from "@/lib/acl";
import {
  userCanApproveZeiterfassung,
  userCanManageAllAbsences,
} from "@/lib/zeiterfassungPermissions";
import { HomeApprovalsCards } from "@/pages/home/HomeApprovalsCards";
import { PersonalAreaCards } from "@/pages/personal/hub/PersonalAreaCards";
import {
  ActiveTimerBanner,
  PersonalKpiStrip,
  SubcontractorOpenCard,
  TeamAbsencesCard,
  UpcomingAbsencesCard,
} from "@/pages/personal/hub/PersonalHubWidgets";
import { usePersonalSelfData } from "@/pages/personal/hub/usePersonalSelfData";

const QUICK_ACTION_CLASS =
  "inline-flex items-center gap-1.5 rounded-full border border-separator bg-card px-3 py-1.5 text-xs font-medium text-label transition hover:border-accent hover:text-accent";

function greeting(now = new Date()): string {
  const h = now.getHours();
  if (h < 11) return "Guten Morgen";
  if (h < 17) return "Guten Tag";
  return "Guten Abend";
}

function firstName(user: { name?: string | null; displayName?: string | null } | null): string {
  const raw = String(user?.name || user?.displayName || "").trim();
  if (!raw) return "";
  // „Nachname, Vorname“ → Vorname
  if (raw.includes(",")) return raw.split(",")[1]?.trim().split(/\s+/)[0] ?? "";
  return raw.split(/\s+/)[0] ?? "";
}

export function PersonalIndexPage() {
  const { user } = useCurrentUser();
  const groups = useMemo(() => normGroups((user?.groups as string[] | undefined) ?? []), [user]);
  const { visibleItems, showHub, loading: navLoading } = usePersonalNavigation();
  const [cards, setCards] = useState<PersonalHubCard[]>([]);
  const [cardsLoading, setCardsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!user) return;
    (async () => {
      setCardsLoading(true);
      try {
        const visible = await resolveVisiblePersonalHubCards(user, groups);
        if (!cancelled) setCards(visible);
      } finally {
        if (!cancelled) setCardsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, groups]);

  const login = useMemo(() => getUserLocalLogin(user), [user]);
  const hasMeinBereich = cards.some((c) => c.key === "mein-bereich");
  const hasNachunternehmer = cards.some((c) => c.key === "nachunternehmer");
  const canApprove = userCanApproveZeiterfassung(user);
  const canSeeTeam = userCanManageAllAbsences(user);

  const { data: self, loading: selfLoading } = usePersonalSelfData(hasMeinBereich ? login : "");

  const loading = navLoading || cardsLoading;

  if (loading) {
    return (
      <AppShell title="Personal" subtitle="Lade Bereiche…">
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      </AppShell>
    );
  }

  if (visibleItems.length === 0) {
    return (
      <AppShell title="Personal">
        <Alert variant="info" title="Kein Zugriff">
          Für Ihre Rolle ist der Personal-Bereich nicht verfügbar.
        </Alert>
      </AppShell>
    );
  }

  if (!showHub && visibleItems.length === 1) {
    const target = visibleItems[0];
    if (!target) {
      return (
        <AppShell title="Personal">
          <Alert variant="info" title="Kein Zugriff">
            Für Ihre Rolle ist der Personal-Bereich nicht verfügbar.
          </Alert>
        </AppShell>
      );
    }
    if (target.internal) {
      return <Navigate to={target.href} replace />;
    }
    window.location.replace(target.href);
    return null;
  }

  const name = firstName(user);
  const subtitle = `${greeting()}${name ? `, ${name}` : ""} – Zeiterfassung, Abwesenheiten und Team an einem Ort.`;
  const showSideColumn = canApprove || canSeeTeam || hasNachunternehmer;

  return (
    <AppShell
      title="Personal"
      subtitle={subtitle}
      actions={
        hasMeinBereich ? (
          <>
            <Link to="/personal/me" className={QUICK_ACTION_CLASS}>
              <ClockIcon className="h-4 w-4" aria-hidden />
              Zeit erfassen
            </Link>
            <Link to="/personal/me?tab=absence" className={QUICK_ACTION_CLASS}>
              <CalendarDaysIcon className="h-4 w-4" aria-hidden />
              Abwesenheit beantragen
            </Link>
          </>
        ) : undefined
      }
    >
      {hasMeinBereich ? <ActiveTimerBanner timer={self.timer} /> : null}

      {hasMeinBereich ? (
        <Card
          title="Mein Überblick"
          description="Ihr aktueller Stand auf einen Blick – Details in „Mein Bereich“."
          actions={
            <Link
              to="/personal/me"
              className="inline-flex items-center rounded-full border border-accent px-3 py-1 text-xs font-medium text-accent hover:bg-accent/10"
            >
              Mein Bereich
            </Link>
          }
          bodyClassName="!p-4"
        >
          <PersonalKpiStrip data={self} loading={selfLoading} />
        </Card>
      ) : null}

      <PersonalAreaCards cards={cards} />

      <div className={showSideColumn ? "grid gap-6 xl:grid-cols-2 xl:items-start" : "grid gap-6"}>
        {hasMeinBereich ? (
          <UpcomingAbsencesCard absences={self.absences} loading={selfLoading} />
        ) : null}
        {canApprove ? <HomeApprovalsCards enabled /> : null}
        <TeamAbsencesCard enabled={canSeeTeam} />
        <SubcontractorOpenCard enabled={hasNachunternehmer} />
      </div>
    </AppShell>
  );
}
